import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import LayoutHOC from "./LayoutHOC";

const BASE_URL = process.env.REACT_APP_API_URL || '/';

const Settings = () => {
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  
  const [smtpSettings, setSmtpSettings] = useState({
    mail_driver: "smtp",
    mail_host: "",
    mail_port: "587",
    mail_username: "",
    mail_password: "",
    mail_encryption: "tls",
    mail_from_address: "",
    mail_from_name: "",
    mail_sendmail_path: "/usr/sbin/sendmail -bs -i",
    mailgun_domain: "",
    mailgun_secret: "",
    mailgun_endpoint: "api.mailgun.net",
    mailgun_scheme: "https",
  });

  const [generalSettings, setGeneralSettings] = useState({
    app_name: "Naka Job Portal",
    app_email: "",
    app_phone: "",
    app_address: "",
  });

  const [brandingSettings, setBrandingSettings] = useState({
    logo_url: "",
    favicon_url: "",
    primary_color: "#3B82F6",
    secondary_color: "#1E40AF",
  });

  const [signinSettings, setSigninSettings] = useState({
    signin_title: "Welcome Back",
    signin_subtitle: "Sign in to your account to continue",
    signin_background_color: "#F8FAFC",
    show_signup_link: true,
    footer_text: "",
  });

  const [profileSettings, setProfileSettings] = useState({
    require_phone_verification: true,
    require_email_verification: true,
    allow_profile_photo: true,
    max_resume_size: 5,
    allowed_resume_formats: "pdf,doc,docx",
  });

  const [backupData, setBackupData] = useState({
    backups: [],
    app_version: "1.0.0",
    laravel_version: "",
    php_version: "",
  });
  const [backupLoading, setBackupLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/settings`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.smtp) setSmtpSettings(data.smtp);
        if (data.general) setGeneralSettings(data.general);
        if (data.branding) setBrandingSettings(data.branding);
        if (data.signin) setSigninSettings(data.signin);
        if (data.profile) setProfileSettings(data.profile);
      }
    } catch (error) {
      console.log("Settings not configured yet");
    }
  };

  const handleSave = async (type, data) => {
    setLoading(true);
    setSaved(false);
    try {
      const response = await fetch(`${BASE_URL}api/settings/${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error(`Error saving ${type} settings:`, error);
    }
    setLoading(false);
  };

  const handleFileUpload = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    try {
      const response = await fetch(`${BASE_URL}api/settings/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user?.token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        if (type === 'logo') {
          setBrandingSettings({ ...brandingSettings, logo_url: data.url });
        } else if (type === 'favicon') {
          setBrandingSettings({ ...brandingSettings, favicon_url: data.url });
        }
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleTestEmail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}api/settings/test-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ email: user?.email }),
      });
      const data = await response.json();
      alert(data.message || "Test email sent!");
    } catch (error) {
      alert("Failed to send test email");
    }
    setLoading(false);
  };

  const fetchBackupInfo = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/settings/backup`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBackupData(data);
      }
    } catch (error) {
      console.error("Error fetching backup info:", error);
    }
  };

  const handleExportBackup = async () => {
    setBackupLoading(true);
    setExportProgress("Creating backup...");
    try {
      const response = await fetch(`${BASE_URL}api/settings/backup/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ include_database: true, include_uploads: true }),
      });
      const data = await response.json();
      if (data.success) {
        setExportProgress("Backup created successfully!");
        fetchBackupInfo();
        setTimeout(() => setExportProgress(null), 3000);
      } else {
        setExportProgress(`Error: ${data.message}`);
        setTimeout(() => setExportProgress(null), 5000);
      }
    } catch (error) {
      setExportProgress("Export failed");
      setTimeout(() => setExportProgress(null), 3000);
    }
    setBackupLoading(false);
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await fetch(`${BASE_URL}api/settings/backup/download/${filename}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        alert(`Download failed: ${errorData.message || 'Unknown error'}`);
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download backup");
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete this backup?\n${filename}`)) return;
    
    try {
      const response = await fetch(`${BASE_URL}api/settings/backup/${filename}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      if (response.ok) {
        fetchBackupInfo();
      }
    } catch (error) {
      console.error("Error deleting backup:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "backup") {
      fetchBackupInfo();
    }
  }, [activeTab]);

  const tabs = [
    { id: "general", label: "General", icon: "bi-gear" },
    { id: "branding", label: "Branding & Logo", icon: "bi-palette" },
    { id: "signin", label: "Sign-in Page", icon: "bi-box-arrow-in-right" },
    { id: "profile", label: "Admin Profile", icon: "bi-person-badge" },
    { id: "smtp", label: "Email / SMTP", icon: "bi-envelope" },
    { id: "api", label: "API Keys", icon: "bi-key" },
    { id: "backup", label: "System Backup", icon: "bi-cloud-arrow-down" },
  ];

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
              <i className="bi bi-sliders text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Settings
              </h1>
              <p className="text-gray-500">Configure your application settings and preferences</p>
            </div>
          </div>
        </div>

        {saved && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3 animate-fade-in">
            <i className="bi bi-check-circle-fill text-emerald-500 text-xl"></i>
            <span className="text-emerald-700 font-medium">Settings saved successfully!</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-100 overflow-x-auto">
            <nav className="flex px-4 gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-violet-500 text-violet-600 bg-violet-50/50"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <i className={`bi ${tab.icon}`}></i>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "general" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <i className="bi bi-info-circle text-violet-500"></i>
                  <span className="text-sm">Configure basic application information</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Application Name</label>
                    <input
                      type="text"
                      value={generalSettings.app_name}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, app_name: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                    <input
                      type="email"
                      value={generalSettings.app_email}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, app_email: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                    <input
                      type="text"
                      value={generalSettings.app_phone}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, app_phone: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <textarea
                      value={generalSettings.app_address}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, app_address: e.target.value })}
                      rows={3}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                </div>
                <div className="pt-4">
                  <button
                    onClick={() => handleSave('general', generalSettings)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
                  >
                    <i className="bi bi-check-lg"></i>
                    {loading ? "Saving..." : "Save General Settings"}
                  </button>
                </div>
              </div>
            )}

            {activeTab === "branding" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <i className="bi bi-info-circle text-violet-500"></i>
                  <span className="text-sm">Customize your application's visual identity</span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="p-6 border border-gray-200 rounded-xl bg-gray-50/50">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <i className="bi bi-image text-violet-500"></i>
                      Main Logo
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="w-32 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-white overflow-hidden">
                        {brandingSettings.logo_url ? (
                          <img src={brandingSettings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                        ) : (
                          <i className="bi bi-image text-3xl text-gray-300"></i>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          ref={logoInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'logo')}
                        />
                        <button
                          onClick={() => logoInputRef.current?.click()}
                          className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          <i className="bi bi-upload mr-2"></i>
                          Upload Logo
                        </button>
                        <p className="text-xs text-gray-500 mt-2">Recommended: 200x60px, PNG or SVG</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-xl bg-gray-50/50">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <i className="bi bi-app-indicator text-violet-500"></i>
                      Favicon
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-white overflow-hidden">
                        {brandingSettings.favicon_url ? (
                          <img src={brandingSettings.favicon_url} alt="Favicon" className="w-8 h-8 object-contain" />
                        ) : (
                          <i className="bi bi-app text-2xl text-gray-300"></i>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          ref={faviconInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'favicon')}
                        />
                        <button
                          onClick={() => faviconInputRef.current?.click()}
                          className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          <i className="bi bi-upload mr-2"></i>
                          Upload Favicon
                        </button>
                        <p className="text-xs text-gray-500 mt-2">Recommended: 32x32px, ICO or PNG</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandingSettings.primary_color}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, primary_color: e.target.value })}
                        className="w-12 h-12 rounded-lg cursor-pointer border-0"
                      />
                      <input
                        type="text"
                        value={brandingSettings.primary_color}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, primary_color: e.target.value })}
                        className="flex-1 p-3 border border-gray-200 rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandingSettings.secondary_color}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, secondary_color: e.target.value })}
                        className="w-12 h-12 rounded-lg cursor-pointer border-0"
                      />
                      <input
                        type="text"
                        value={brandingSettings.secondary_color}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, secondary_color: e.target.value })}
                        className="flex-1 p-3 border border-gray-200 rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    onClick={() => handleSave('branding', brandingSettings)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
                  >
                    <i className="bi bi-check-lg"></i>
                    {loading ? "Saving..." : "Save Branding Settings"}
                  </button>
                </div>
              </div>
            )}

            {activeTab === "signin" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <i className="bi bi-info-circle text-violet-500"></i>
                  <span className="text-sm">Customize the sign-in page appearance and content</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sign-in Page Title</label>
                      <input
                        type="text"
                        value={signinSettings.signin_title}
                        onChange={(e) => setSigninSettings({ ...signinSettings, signin_title: e.target.value })}
                        placeholder="Welcome Back"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sign-in Page Subtitle</label>
                      <input
                        type="text"
                        value={signinSettings.signin_subtitle}
                        onChange={(e) => setSigninSettings({ ...signinSettings, signin_subtitle: e.target.value })}
                        placeholder="Sign in to your account to continue"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={signinSettings.signin_background_color}
                          onChange={(e) => setSigninSettings({ ...signinSettings, signin_background_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <input
                          type="text"
                          value={signinSettings.signin_background_color}
                          onChange={(e) => setSigninSettings({ ...signinSettings, signin_background_color: e.target.value })}
                          className="flex-1 p-3 border border-gray-200 rounded-xl font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text</label>
                      <textarea
                        value={signinSettings.footer_text}
                        onChange={(e) => setSigninSettings({ ...signinSettings, footer_text: e.target.value })}
                        placeholder="Copyright 2024 Your Company"
                        rows={2}
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={signinSettings.show_signup_link}
                          onChange={(e) => setSigninSettings({ ...signinSettings, show_signup_link: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-500"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">Show Sign-up Link</span>
                      </label>
                    </div>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-xl" style={{ backgroundColor: signinSettings.signin_background_color }}>
                    <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm mx-auto">
                      <div className="text-center mb-6">
                        {brandingSettings.logo_url && (
                          <img src={brandingSettings.logo_url} alt="Logo" className="h-8 mx-auto mb-4" />
                        )}
                        <h3 className="text-xl font-bold text-gray-800">{signinSettings.signin_title || "Welcome Back"}</h3>
                        <p className="text-sm text-gray-500 mt-1">{signinSettings.signin_subtitle || "Sign in to continue"}</p>
                      </div>
                      <div className="space-y-3">
                        <div className="h-10 bg-gray-100 rounded-lg"></div>
                        <div className="h-10 bg-gray-100 rounded-lg"></div>
                        <div className="h-10 bg-violet-500 rounded-lg"></div>
                      </div>
                      {signinSettings.show_signup_link && (
                        <p className="text-center text-sm text-gray-500 mt-4">Don't have an account? <span className="text-violet-500">Sign up</span></p>
                      )}
                      {signinSettings.footer_text && (
                        <p className="text-center text-xs text-gray-400 mt-4">{signinSettings.footer_text}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    onClick={() => handleSave('signin', signinSettings)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
                  >
                    <i className="bi bi-check-lg"></i>
                    {loading ? "Saving..." : "Save Sign-in Settings"}
                  </button>
                </div>
              </div>
            )}

            {activeTab === "profile" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <i className="bi bi-info-circle text-violet-500"></i>
                  <span className="text-sm">Manage your admin account profile and security settings</span>
                </div>

                <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                      {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{user?.name || 'Admin User'}</h3>
                      <p className="text-sm text-gray-500">{user?.email || 'admin@example.com'}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">Super Admin</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800">Account Information</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <input
                        type="text"
                        value={profileSettings.admin_name || user?.name || ''}
                        onChange={(e) => setProfileSettings({ ...profileSettings, admin_name: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <input
                        type="email"
                        value={profileSettings.admin_email || user?.email || ''}
                        onChange={(e) => setProfileSettings({ ...profileSettings, admin_email: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="text"
                        value={profileSettings.admin_phone || ''}
                        onChange={(e) => setProfileSettings({ ...profileSettings, admin_phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800">Change Password</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                      <input
                        type="password"
                        value={profileSettings.current_password || ''}
                        onChange={(e) => setProfileSettings({ ...profileSettings, current_password: e.target.value })}
                        placeholder="Enter current password"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                      <input
                        type="password"
                        value={profileSettings.new_password || ''}
                        onChange={(e) => setProfileSettings({ ...profileSettings, new_password: e.target.value })}
                        placeholder="Enter new password"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                      <input
                        type="password"
                        value={profileSettings.confirm_password || ''}
                        onChange={(e) => setProfileSettings({ ...profileSettings, confirm_password: e.target.value })}
                        placeholder="Confirm new password"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    onClick={() => handleSave('profile', profileSettings)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
                  >
                    <i className="bi bi-check-lg"></i>
                    {loading ? "Saving..." : "Update Admin Profile"}
                  </button>
                </div>
              </div>
            )}

            {activeTab === "smtp" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <i className="bi bi-info-circle text-violet-500"></i>
                  <span className="text-sm">Configure email delivery settings for notifications</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mail Driver</label>
                    <select
                      value={smtpSettings.mail_driver}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_driver: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="smtp">SMTP</option>
                      <option value="sendmail">Sendmail</option>
                      <option value="mailgun">Mailgun</option>
                    </select>
                  </div>
                  {smtpSettings.mail_driver === "smtp" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mail Host</label>
                        <input
                          type="text"
                          value={smtpSettings.mail_host}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_host: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mail Port</label>
                        <input
                          type="text"
                          value={smtpSettings.mail_port}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_port: e.target.value })}
                          placeholder="587"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Encryption</label>
                        <select
                          value={smtpSettings.mail_encryption}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_encryption: e.target.value })}
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        >
                          <option value="tls">TLS</option>
                          <option value="ssl">SSL</option>
                          <option value="">None</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mail Username</label>
                        <input
                          type="text"
                          value={smtpSettings.mail_username}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_username: e.target.value })}
                          placeholder="your-email@gmail.com"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mail Password</label>
                        <input
                          type="password"
                          value={smtpSettings.mail_password}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_password: e.target.value })}
                          placeholder="App Password"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                    </>
                  )}

                  {smtpSettings.mail_driver === "sendmail" && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sendmail Path</label>
                      <input
                        type="text"
                        value={smtpSettings.mail_sendmail_path || ""}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_sendmail_path: e.target.value })}
                        placeholder="/usr/sbin/sendmail -bs -i"
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                  )}

                  {smtpSettings.mail_driver === "mailgun" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mailgun Domain</label>
                        <input
                          type="text"
                          value={smtpSettings.mailgun_domain || ""}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mailgun_domain: e.target.value })}
                          placeholder="mg.yourdomain.com"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mailgun Secret</label>
                        <input
                          type="password"
                          value={smtpSettings.mailgun_secret || ""}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mailgun_secret: e.target.value })}
                          placeholder="key-xxxxxxxxxxxxxxxx"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mailgun Endpoint</label>
                        <input
                          type="text"
                          value={smtpSettings.mailgun_endpoint || ""}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mailgun_endpoint: e.target.value })}
                          placeholder="api.mailgun.net"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mailgun Scheme</label>
                        <select
                          value={smtpSettings.mailgun_scheme || "https"}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, mailgun_scheme: e.target.value })}
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                        >
                          <option value="https">HTTPS</option>
                          <option value="http">HTTP</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Address</label>
                    <input
                      type="email"
                      value={smtpSettings.mail_from_address}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_from_address: e.target.value })}
                      placeholder="noreply@yourcompany.com"
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
                    <input
                      type="text"
                      value={smtpSettings.mail_from_name}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, mail_from_name: e.target.value })}
                      placeholder="Naka Job Portal"
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => handleSave('smtp', smtpSettings)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
                  >
                    <i className="bi bi-check-lg"></i>
                    {loading ? "Saving..." : "Save SMTP Settings"}
                  </button>
                  <button
                    onClick={handleTestEmail}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 disabled:opacity-50 transition-all font-medium"
                  >
                    <i className="bi bi-send"></i>
                    Send Test Email
                  </button>
                </div>
              </div>
            )}

            {activeTab === "api" && (
              <div className="space-y-6">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h3 className="text-amber-800 font-medium mb-2 flex items-center gap-2">
                    <i className="bi bi-exclamation-triangle"></i>
                    Third-Party API Configuration
                  </h3>
                  <p className="text-amber-700 text-sm">
                    API keys for Deepvue (verification) and 2Factor (OTP) are managed through environment variables for security.
                    Contact your administrator to update these credentials.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-shield-check text-emerald-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">Deepvue - Profile Verification</h4>
                        <p className="text-sm text-gray-500">Used for Aadhar, PAN, and GST verification</p>
                      </div>
                    </div>
                    <span className="inline-block px-3 py-1 text-xs bg-gray-200 text-gray-600 rounded-full">Configured via ENV</span>
                  </div>
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-phone text-blue-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">2Factor - OTP Service</h4>
                        <p className="text-sm text-gray-500">Used for mobile OTP authentication</p>
                      </div>
                    </div>
                    <span className="inline-block px-3 py-1 text-xs bg-gray-200 text-gray-600 rounded-full">Configured via ENV</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "backup" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <i className="bi bi-info-circle text-violet-500"></i>
                  <span className="text-sm">Create and manage system backups for deployment or restoration</span>
                </div>

                {exportProgress && (
                  <div className={`p-4 rounded-xl flex items-center gap-3 ${exportProgress.includes('Error') || exportProgress.includes('failed') ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
                    {backupLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    ) : (
                      <i className={`bi ${exportProgress.includes('Error') || exportProgress.includes('failed') ? 'bi-x-circle text-red-500' : 'bi-check-circle text-blue-500'} text-xl`}></i>
                    )}
                    <span className={exportProgress.includes('Error') || exportProgress.includes('failed') ? 'text-red-700' : 'text-blue-700'}>{exportProgress}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                        <i className="bi bi-cloud-arrow-up text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">Export Backup</h3>
                        <p className="text-sm text-gray-500">Create a ZIP file of your system</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Creates a complete backup including code, configuration, database schema, and data. 
                      Use this for deployment to cPanel or other servers.
                    </p>
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <i className="bi bi-check-circle-fill text-emerald-500"></i>
                        Application code and routes
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <i className="bi bi-check-circle-fill text-emerald-500"></i>
                        Database structure and data
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <i className="bi bi-check-circle-fill text-emerald-500"></i>
                        Configuration files (excluding .env secrets)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <i className="bi bi-check-circle-fill text-emerald-500"></i>
                        Frontend build files
                      </div>
                    </div>
                    <button
                      onClick={handleExportBackup}
                      disabled={backupLoading}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
                    >
                      <i className="bi bi-download"></i>
                      {backupLoading ? "Creating Backup..." : "Create System Backup"}
                    </button>
                  </div>

                  <div className="p-6 bg-gradient-to-br from-gray-50 to-slate-100 rounded-xl border border-gray-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-slate-500 rounded-xl flex items-center justify-center shadow-lg">
                        <i className="bi bi-cloud-arrow-down text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">Import Backup</h3>
                        <p className="text-sm text-gray-500">Manual restore required</p>
                      </div>
                    </div>
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <div className="flex items-start gap-2">
                        <i className="bi bi-shield-lock text-amber-600 mt-0.5"></i>
                        <div>
                          <p className="text-sm font-medium text-amber-800">Import Disabled for Security</p>
                          <p className="text-xs text-amber-700 mt-1">
                            For security reasons, importing backups through the web interface is disabled. 
                            Please restore backups manually on your server.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 space-y-2">
                      <p className="font-medium">To restore a backup manually:</p>
                      <ol className="list-decimal list-inside space-y-1 text-gray-500">
                        <li>Download the backup ZIP file</li>
                        <li>Extract it on your server</li>
                        <li>Copy files to your application directory</li>
                        <li>Run <code className="bg-gray-100 px-1 rounded text-xs">php artisan migrate</code></li>
                        <li>Run <code className="bg-gray-100 px-1 rounded text-xs">php artisan config:clear</code></li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-white rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <i className="bi bi-archive text-violet-500"></i>
                      Available Backups
                    </h3>
                    <div className="text-xs text-gray-500">
                      PHP {backupData.php_version} | Laravel {backupData.laravel_version}
                    </div>
                  </div>

                  {backupData.backups.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <i className="bi bi-inbox text-4xl mb-3 block text-gray-300"></i>
                      <p>No backups created yet</p>
                      <p className="text-sm">Create your first backup using the Export button above</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {backupData.backups.map((backup, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                              <i className="bi bi-file-earmark-zip text-violet-600"></i>
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{backup.name}</p>
                              <p className="text-xs text-gray-500">{backup.created_at} - {backup.size}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadBackup(backup.name)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Download"
                            >
                              <i className="bi bi-download"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteBackup(backup.name)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h4 className="text-amber-800 font-medium mb-2 flex items-center gap-2">
                    <i className="bi bi-exclamation-triangle"></i>
                    Important Notes
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                    <li>Backups exclude sensitive files like .env (you'll need to configure those separately)</li>
                    <li>Always test backups on a staging environment before restoring to production</li>
                    <li>For cPanel deployment, upload the backup and extract it to your public_html folder</li>
                    <li>Remember to run <code className="bg-amber-100 px-1 rounded">php artisan migrate</code> after importing</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(Settings);
